import { useState, useRef, useEffect } from 'react';

interface FeatureHighlightsProps {
  onGenerateEbook: () => void;
  onBrowseTemplates: () => void;
  onLogin: () => void;
}

// ─── Shared Backdrop ─────────────────────────────────────────────────────────
function FeatureModal({ onClose, children, width = 'max-w-2xl' }: { onClose: () => void; children: React.ReactNode; width?: string }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className={`relative bg-white rounded-3xl shadow-2xl w-full ${width} max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ─── 1. AI Writing Modal ─────────────────────────────────────────────────────
function AIWritingModal({ onClose, onGenerate }: { onClose: () => void; onGenerate: () => void }) {
  const [tab, setTab] = useState<'chapter' | 'outline' | 'summary'>('chapter');
  const [prompt, setPrompt] = useState('');
  const [genre, setGenre] = useState('Business & Entrepreneurship');
  const [tone, setTone] = useState('Professional');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generated, setGenerated] = useState('');

  const samples: Record<string, Record<string, string>> = {
    chapter: {
      'Business & Entrepreneurship': `**Chapter 3: Building Your First Revenue Stream**\n\nThe moment your business generates its first dollar, something shifts. It's no longer a project—it's a company. But getting to that first dollar is where most founders stumble.\n\nThe key is to resist the urge to build the perfect product before selling. Instead, sell first, build second. This approach, known as "pre-selling," lets you validate demand before investing months of development time...\n\nIn this chapter, we'll walk through three battle-tested revenue models that founders at every stage can implement within 30 days: consulting packages, digital downloads, and subscription communities.`,
      'Self-Help & Personal Development': `**Chapter 5: The Power of Micro-Commitments**\n\nWillpower is overrated. Research from Stanford's psychology department consistently shows that people who rely on willpower to achieve their goals fail at dramatically higher rates than those who design their environment for success.\n\nThe secret isn't trying harder—it's thinking smaller. A micro-commitment is any action so small it feels almost embarrassing to skip...`,
      default: `**Chapter 1: Introduction**\n\nEvery great story begins with a single question worth answering. The ideas in this chapter lay the foundation for everything that follows—setting the stage for a journey that will challenge assumptions, build new skills, and ultimately transform your perspective.\n\nLet's begin by examining the core principles that underpin this entire work...`,
    },
    outline: {
      default: `**Full eBook Outline**\n\n1. Introduction — Why This Topic Matters Now\n2. Part One: Foundations\n   2.1 Core Concepts & Terminology\n   2.2 Historical Context\n   2.3 The Modern Landscape\n3. Part Two: Building Your Framework\n   3.1 Assessment & Starting Point\n   3.2 The 5-Step Method\n   3.3 Common Pitfalls to Avoid\n4. Part Three: Advanced Strategies\n   4.1 Scaling What Works\n   4.2 Case Studies\n   4.3 Expert Interviews\n5. Conclusion — Your 90-Day Action Plan\n6. Appendix & Resources`,
    },
    summary: {
      default: `**Executive Summary**\n\nThis eBook distills 10 years of research and real-world application into a clear, actionable framework. The core thesis: sustainable success in this domain isn't about working harder—it's about building smarter systems.\n\nKey takeaways:\n• The 3-pillar framework that top performers rely on\n• Why conventional advice fails 80% of readers\n• A repeatable 5-step process you can start today\n• Real case studies with measurable outcomes\n\nIdeal for beginners through intermediate practitioners, this guide provides both conceptual depth and practical exercises at the end of every chapter.`,
    },
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setGenerated('');
    const content = samples[tab][genre] ?? samples[tab].default ?? samples.chapter.default;
    let idx = 0;
    const interval = setInterval(() => {
      idx += 3;
      setGenerated(content.slice(0, idx));
      if (idx >= content.length) { clearInterval(interval); setIsGenerating(false); }
    }, 18);
  };

  const tabs = [
    { id: 'chapter', label: 'Write Chapter', icon: 'ri-file-text-line' },
    { id: 'outline', label: 'Build Outline', icon: 'ri-list-check' },
    { id: 'summary', label: 'Summarize', icon: 'ri-file-reduce-line' },
  ] as const;

  const genres = ['Business & Entrepreneurship', 'Self-Help & Personal Development', 'Technology & Programming', 'Fiction & Storytelling', 'Health & Wellness', 'Finance & Investing'];
  const tones = ['Professional', 'Conversational', 'Academic', 'Inspiring', 'Storytelling', 'Technical'];

  return (
    <FeatureModal onClose={onClose}>
      {/* Header */}
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-orange-500 flex items-center justify-center">
            <i className="ri-magic-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">AI Writing</h2>
            <p className="text-sm text-slate-500">Generate chapters, outlines, and summaries in seconds</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer transition-colors">
          <i className="ri-close-line text-slate-600"></i>
        </button>
      </div>

      <div className="p-6 space-y-5">
        {/* Tabs */}
        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setGenerated(''); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${tab === t.id ? 'bg-white text-orange-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <i className={`${t.icon} text-sm`}></i>
              {t.label}
            </button>
          ))}
        </div>

        {/* Topic Input */}
        <div>
          <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Topic / Prompt</label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder={tab === 'chapter' ? 'e.g. "Building recurring revenue as a solopreneur"' : tab === 'outline' ? 'e.g. "A complete guide to mastering public speaking"' : 'Paste your draft text here...'}
            rows={3}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-transparent resize-none"
          />
        </div>

        {/* Genre + Tone */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Genre</label>
            <select
              value={genre}
              onChange={e => setGenre(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-300 cursor-pointer"
            >
              {genres.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Writing Tone</label>
            <div className="flex flex-wrap gap-1.5">
              {tones.map(t => (
                <button
                  key={t}
                  onClick={() => setTone(t)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${tone === t ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Output */}
        {generated && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-sparkling-2-line text-orange-500 text-sm"></i>
              </div>
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">AI Output</span>
              <span className="ml-auto text-xs text-slate-400">{genre} · {tone}</span>
            </div>
            <pre className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-sans">{generated}{isGenerating && <span className="inline-block w-0.5 h-4 bg-orange-400 animate-pulse ml-0.5 align-middle"></span>}</pre>
          </div>
        )}

        {/* CTAs */}
        <div className="flex gap-3">
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            {isGenerating ? <><i className="ri-loader-4-line animate-spin text-base"></i> Generating...</> : <><i className="ri-magic-line text-base"></i> Generate with AI</>}
          </button>
          <button
            onClick={onGenerate}
            className="px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center gap-2"
          >
            <i className="ri-book-2-line text-base"></i>
            Full eBook
          </button>
        </div>
      </div>
    </FeatureModal>
  );
}

// ─── 2. Multi-Format Export Modal ─────────────────────────────────────────────
function ExportModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [selectedFormat, setSelectedFormat] = useState('PDF');
  const [quality, setQuality] = useState<'standard' | 'high' | 'print'>('high');
  const [drm, setDrm] = useState(false);
  const [embedFont, setEmbedFont] = useState(true);

  const formats = [
    { id: 'PDF', label: 'PDF', icon: 'ri-file-pdf-line', color: '#ef4444', desc: 'Universal, print-ready', size: '~2.4 MB', badge: 'Most Popular' },
    { id: 'EPUB', label: 'EPUB 3', icon: 'ri-book-line', color: '#8b5cf6', desc: 'Reflowable, e-readers', size: '~850 KB', badge: null },
    { id: 'DOCX', label: 'Word DOCX', icon: 'ri-file-word-line', color: '#3b82f6', desc: 'Editable document', size: '~1.1 MB', badge: null },
    { id: 'KINDLE', label: 'Kindle MOBI', icon: 'ri-amazon-line', color: '#f59e0b', desc: 'Kindle-ready format', size: '~900 KB', badge: 'New' },
  ];

  const qualityOptions = [
    { id: 'standard', label: 'Standard', desc: '72 dpi · Web optimized' },
    { id: 'high', label: 'High', desc: '150 dpi · Balanced' },
    { id: 'print', label: 'Print', desc: '300 dpi · Press ready' },
  ] as const;

  return (
    <FeatureModal onClose={onClose}>
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-red-500 flex items-center justify-center">
            <i className="ri-file-download-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Multi-Format Export</h2>
            <p className="text-sm text-slate-500">Export to PDF, EPUB, DOCX, and Kindle formats</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>

      <div className="p-6 space-y-6">
        {/* Format picker */}
        <div>
          <label className="block text-xs font-bold text-slate-700 mb-3 uppercase tracking-wide">Select Format</label>
          <div className="grid grid-cols-2 gap-3">
            {formats.map(fmt => (
              <button
                key={fmt.id}
                onClick={() => setSelectedFormat(fmt.id)}
                className={`relative p-4 rounded-2xl border-2 text-left transition-all cursor-pointer ${selectedFormat === fmt.id ? 'border-current shadow-lg' : 'border-slate-200 hover:border-slate-300'}`}
                style={selectedFormat === fmt.id ? { borderColor: fmt.color, backgroundColor: fmt.color + '08' } : {}}
              >
                {fmt.badge && (
                  <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-bold rounded-full ${fmt.badge === 'Most Popular' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{fmt.badge}</span>
                )}
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: fmt.color + '20' }}>
                  <i className={`${fmt.icon} text-2xl`} style={{ color: fmt.color }}></i>
                </div>
                <p className="font-bold text-slate-900 text-sm">{fmt.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{fmt.desc}</p>
                <p className="text-xs font-semibold mt-1.5" style={{ color: fmt.color }}>{fmt.size}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Quality */}
        <div>
          <label className="block text-xs font-bold text-slate-700 mb-3 uppercase tracking-wide">Image Quality</label>
          <div className="grid grid-cols-3 gap-2">
            {qualityOptions.map(q => (
              <button
                key={q.id}
                onClick={() => setQuality(q.id)}
                className={`p-3 rounded-xl border-2 text-center transition-all cursor-pointer ${quality === q.id ? 'border-orange-400 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <p className={`text-sm font-bold ${quality === q.id ? 'text-orange-700' : 'text-slate-700'}`}>{q.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{q.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <div
                onClick={() => setDrm(v => !v)}
                className={`w-10 h-6 rounded-full transition-all relative ${drm ? 'bg-orange-500' : 'bg-slate-300'}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${drm ? 'translate-x-4' : ''}`}></div>
              </div>
              <span className="text-sm font-semibold text-slate-700">DRM Protection</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <div
                onClick={() => setEmbedFont(v => !v)}
                className={`w-10 h-6 rounded-full transition-all relative ${embedFont ? 'bg-orange-500' : 'bg-slate-300'}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${embedFont ? 'translate-x-4' : ''}`}></div>
              </div>
              <span className="text-sm font-semibold text-slate-700">Embed Fonts</span>
            </label>
          </div>
        </div>

        <button
          onClick={onLogin}
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold rounded-xl transition-all hover:opacity-90 cursor-pointer text-sm shadow-lg shadow-red-200"
        >
          <i className="ri-download-2-line text-base"></i>
          Export as {selectedFormat} — Sign in to Download
        </button>
      </div>
    </FeatureModal>
  );
}

// ─── 3. Collaboration Modal ─────────────────────────────────────────────────
function CollaborationModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'Co-author' | 'Editor' | 'Reviewer'>('Editor');
  const [message, setMessage] = useState('');

  const mockCollaborators = [
    { name: 'Sarah Mitchell', role: 'Co-author', avatar: 'https://public.readdy.ai/ai/img_res/3b3679713c8326cd27d0b07abe8d36d3.jpg', status: 'active', lastSeen: 'Now' },
    { name: 'James Forrest', role: 'Editor', avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20headshot%20clean%20background%20glasses%20confident&width=80&height=80&seq=collab-2&orientation=squarish', status: 'active', lastSeen: '5m ago' },
    { name: 'Priya Sharma', role: 'Reviewer', avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20headshot%20clean%20background%20confident%20smile&width=80&height=80&seq=collab-3&orientation=squarish', status: 'offline', lastSeen: '2h ago' },
  ];

  const roles = ['Co-author', 'Editor', 'Reviewer'] as const;
  const roleInfo = {
    'Co-author': { icon: 'ri-edit-2-line', desc: 'Full write & edit access', color: '#6366f1' },
    'Editor': { icon: 'ri-scissors-line', desc: 'Edit only, cannot publish', color: '#10b981' },
    'Reviewer': { icon: 'ri-chat-3-line', desc: 'Comment only, read access', color: '#f59e0b' },
  };

  return (
    <FeatureModal onClose={onClose}>
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center">
            <i className="ri-team-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Collaboration</h2>
            <p className="text-sm text-slate-500">Invite co-authors, editors, and reviewers</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>

      <div className="p-6 space-y-6">
        {/* Active Collaborators */}
        <div>
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3">Active Collaborators</p>
          <div className="space-y-2">
            {mockCollaborators.map(c => (
              <div key={c.name} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="relative">
                  <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-full object-cover" />
                  <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${c.status === 'active' ? 'bg-emerald-400' : 'bg-slate-300'}`}></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{c.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: roleInfo[c.role as keyof typeof roleInfo]?.color + '15', color: roleInfo[c.role as keyof typeof roleInfo]?.color }}>
                      {c.role}
                    </span>
                    <span className="text-xs text-slate-400">{c.lastSeen}</span>
                  </div>
                </div>
                <button className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer">
                  <i className="ri-delete-bin-6-line text-sm"></i>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Invite Form */}
        <div className="bg-slate-50 rounded-2xl p-4 space-y-4">
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Invite New Member</p>
          <div>
            <label className="block text-xs text-slate-500 font-semibold mb-1.5">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="colleague@example.com"
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 font-semibold mb-1.5">Role</label>
            <div className="grid grid-cols-3 gap-2">
              {roles.map(r => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className={`p-3 rounded-xl border-2 text-center transition-all cursor-pointer ${role === r ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                >
                  <div className="w-7 h-7 rounded-lg mx-auto mb-1.5 flex items-center justify-center" style={{ backgroundColor: roleInfo[r].color + '20' }}>
                    <i className={`${roleInfo[r].icon} text-sm`} style={{ color: roleInfo[r].color }}></i>
                  </div>
                  <p className={`text-xs font-bold ${role === r ? 'text-emerald-700' : 'text-slate-700'}`}>{r}</p>
                  <p className="text-xs text-slate-400 mt-0.5 leading-tight">{roleInfo[r].desc}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 font-semibold mb-1.5">Personal Message (optional)</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Hi! I'd love your help with this eBook..."
              rows={2}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none"
            />
          </div>
        </div>

        <button
          onClick={onLogin}
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl cursor-pointer text-sm transition-colors"
        >
          <i className="ri-send-plane-line text-base"></i>
          Send Invitation — Sign In to Collaborate
        </button>
      </div>
    </FeatureModal>
  );
}

// ─── 4. AI Cover Design Modal ────────────────────────────────────────────────
function CoverDesignModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [style, setStyle] = useState('Minimalist');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [palette, setPalette] = useState('#6366f1');
  const [generated, setGenerated] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const styles = [
    { id: 'Minimalist', icon: 'ri-subtract-line', color: '#94a3b8' },
    { id: 'Bold & Dark', icon: 'ri-moon-line', color: '#1e293b' },
    { id: 'Illustrated', icon: 'ri-brush-line', color: '#ec4899' },
    { id: 'Photographic', icon: 'ri-camera-line', color: '#0ea5e9' },
    { id: 'Vintage', icon: 'ri-time-line', color: '#d97706' },
    { id: 'Neon / Futuristic', icon: 'ri-rocket-line', color: '#8b5cf6' },
  ];

  const palettes = ['#6366f1', '#ec4899', '#f97316', '#10b981', '#0ea5e9', '#1e293b', '#d97706', '#ef4444'];

  const previewImages: Record<string, string> = {
    'Minimalist': 'https://readdy.ai/api/search-image?query=minimalist%20ebook%20cover%20clean%20white%20space%20elegant%20serif%20typography%20simple%20subtle%20design%20professional%20book%20cover%20mockup&width=300&height=400&seq=cover-preview-1&orientation=portrait',
    'Bold & Dark': 'https://readdy.ai/api/search-image?query=bold%20dark%20ebook%20cover%20powerful%20dramatic%20black%20charcoal%20background%20strong%20typography%20professional%20book%20cover%20mockup&width=300&height=400&seq=cover-preview-2&orientation=portrait',
    'Illustrated': 'https://readdy.ai/api/search-image?query=illustrated%20ebook%20cover%20creative%20artistic%20beautiful%20illustration%20colorful%20digital%20art%20professional%20book%20cover%20mockup&width=300&height=400&seq=cover-preview-3&orientation=portrait',
    'Photographic': 'https://readdy.ai/api/search-image?query=photographic%20ebook%20cover%20beautiful%20photography%20full%20bleed%20image%20professional%20book%20cover%20mockup%20stunning&width=300&height=400&seq=cover-preview-4&orientation=portrait',
    'Vintage': 'https://readdy.ai/api/search-image?query=vintage%20ebook%20cover%20retro%20aged%20paper%20texture%20warm%20tones%20classic%20serif%20typography%20book%20cover%20mockup&width=300&height=400&seq=cover-preview-5&orientation=portrait',
    'Neon / Futuristic': 'https://readdy.ai/api/search-image?query=neon%20futuristic%20ebook%20cover%20dark%20background%20glowing%20neon%20colors%20sci-fi%20tech%20modern%20book%20cover%20mockup&width=300&height=400&seq=cover-preview-6&orientation=portrait',
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => { setIsGenerating(false); setGenerated(true); }, 1500);
  };

  return (
    <FeatureModal onClose={onClose} width="max-w-3xl">
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-sky-500 flex items-center justify-center">
            <i className="ri-image-ai-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">AI Cover Design</h2>
            <p className="text-sm text-slate-500">Generate stunning, genre-specific cover art instantly</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-5 gap-6">
          {/* Left: controls */}
          <div className="col-span-3 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Book Title</label>
                <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Your eBook Title" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-300" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Author Name</label>
                <input value={author} onChange={e => setAuthor(e.target.value)} placeholder="Your Name" className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-300" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-3 uppercase tracking-wide">Cover Style</label>
              <div className="grid grid-cols-3 gap-2">
                {styles.map(s => (
                  <button
                    key={s.id}
                    onClick={() => { setStyle(s.id); setGenerated(false); }}
                    className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all cursor-pointer ${style === s.id ? 'border-sky-400 bg-sky-50' : 'border-slate-200 bg-slate-50 hover:border-slate-300'}`}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.color + '20' }}>
                      <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
                    </div>
                    <span className={`text-xs font-semibold truncate ${style === s.id ? 'text-sky-700' : 'text-slate-700'}`}>{s.id}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-3 uppercase tracking-wide">Color Palette</label>
              <div className="flex gap-2 flex-wrap">
                {palettes.map(p => (
                  <button
                    key={p}
                    onClick={() => setPalette(p)}
                    className={`w-8 h-8 rounded-full border-4 transition-all cursor-pointer ${palette === p ? 'border-white scale-125 shadow-lg' : 'border-transparent'}`}
                    style={{ backgroundColor: p, outlineOffset: '2px', outline: palette === p ? `2px solid ${p}` : 'none' }}
                  ></button>
                ))}
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold rounded-xl transition-colors cursor-pointer text-sm"
            >
              {isGenerating ? <><i className="ri-loader-4-line animate-spin"></i> Generating Cover...</> : <><i className="ri-magic-line"></i> Generate Cover</>}
            </button>
          </div>

          {/* Right: preview */}
          <div className="col-span-2">
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3">Preview</p>
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 aspect-[3/4] bg-slate-100">
              <img
                src={previewImages[style]}
                alt="Cover preview"
                className={`w-full h-full object-cover transition-opacity duration-500 ${isGenerating ? 'opacity-30' : 'opacity-100'}`}
              />
              {isGenerating && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs font-bold text-sky-600 bg-white/80 px-3 py-1 rounded-full">AI is designing...</span>
                </div>
              )}
              {generated && (title || author) && (
                <div className="absolute inset-0 flex flex-col justify-end p-4" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)' }}>
                  {title && <p className="text-white font-extrabold text-lg leading-tight drop-shadow">{title}</p>}
                  {author && <p className="text-white/80 text-sm mt-1">{author}</p>}
                </div>
              )}
              <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-xs rounded-lg font-semibold">{style}</div>
            </div>
            {generated && (
              <button onClick={onLogin} className="w-full mt-3 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl cursor-pointer hover:bg-slate-800 transition-colors">
                Use This Cover
              </button>
            )}
          </div>
        </div>
      </div>
    </FeatureModal>
  );
}

// ─── 5. Auto-Translation Modal ────────────────────────────────────────────────
function TranslationModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const languages = [
    { code: 'es', name: 'Spanish', flag: '🇪🇸', speakers: '485M' },
    { code: 'fr', name: 'French', flag: '🇫🇷', speakers: '280M' },
    { code: 'de', name: 'German', flag: '🇩🇪', speakers: '135M' },
    { code: 'zh', name: 'Chinese (Simplified)', flag: '🇨🇳', speakers: '1.1B' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵', speakers: '128M' },
    { code: 'pt', name: 'Portuguese', flag: '🇧🇷', speakers: '260M' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦', speakers: '420M' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺', speakers: '258M' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷', speakers: '77M' },
    { code: 'it', name: 'Italian', flag: '🇮🇹', speakers: '90M' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳', speakers: '615M' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱', speakers: '25M' },
    { code: 'pl', name: 'Polish', flag: '🇵🇱', speakers: '50M' },
    { code: 'sv', name: 'Swedish', flag: '🇸🇪', speakers: '10M' },
    { code: 'tr', name: 'Turkish', flag: '🇹🇷', speakers: '88M' },
    { code: 'id', name: 'Indonesian', flag: '🇮🇩', speakers: '270M' },
    { code: 'vi', name: 'Vietnamese', flag: '🇻🇳', speakers: '97M' },
    { code: 'th', name: 'Thai', flag: '🇹🇭', speakers: '70M' },
    { code: 'uk', name: 'Ukrainian', flag: '🇺🇦', speakers: '45M' },
    { code: 'ro', name: 'Romanian', flag: '🇷🇴', speakers: '26M' },
  ];

  const filtered = languages.filter(l => l.name.toLowerCase().includes(search.toLowerCase()) || l.code.toLowerCase().includes(search.toLowerCase()));
  const toggleLang = (code: string) => setSelected(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);

  return (
    <FeatureModal onClose={onClose} width="max-w-2xl">
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center">
            <i className="ri-translate-2 text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Auto-Translation</h2>
            <p className="text-sm text-slate-500">Translate your eBook into 50+ languages globally</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>

      <div className="p-6 space-y-5">
        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-3">
          {[{ icon: 'ri-global-line', val: '50+', label: 'Languages' }, { icon: 'ri-time-line', val: '~3 min', label: 'Per eBook' }, { icon: 'ri-star-line', val: '99.2%', label: 'Accuracy' }].map(s => (
            <div key={s.label} className="flex flex-col items-center p-3 bg-amber-50 rounded-2xl border border-amber-100">
              <div className="w-8 h-8 flex items-center justify-center mb-1">
                <i className={`${s.icon} text-amber-500 text-lg`}></i>
              </div>
              <p className="font-extrabold text-amber-700 text-lg leading-none">{s.val}</p>
              <p className="text-xs text-amber-600 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Selected */}
        {selected.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-slate-500">Selected:</span>
            {selected.map(code => {
              const lang = languages.find(l => l.code === code);
              return lang ? (
                <span key={code} className="flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                  {lang.flag} {lang.name}
                  <button onClick={() => toggleLang(code)} className="ml-1 cursor-pointer hover:text-amber-900"><i className="ri-close-line text-xs"></i></button>
                </span>
              ) : null;
            })}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search languages..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {/* Language grid */}
        <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
          {filtered.map(lang => (
            <button
              key={lang.code}
              onClick={() => toggleLang(lang.code)}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${selected.includes(lang.code) ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-slate-50 hover:border-slate-300'}`}
            >
              <span className="text-2xl leading-none">{lang.flag}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{lang.name}</p>
                <p className="text-xs text-slate-400">{lang.speakers} speakers</p>
              </div>
              {selected.includes(lang.code) && <i className="ri-check-line text-amber-500 text-sm flex-shrink-0"></i>}
            </button>
          ))}
        </div>

        <button
          onClick={onLogin}
          disabled={selected.length === 0}
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl cursor-pointer text-sm transition-colors"
        >
          <i className="ri-translate-2 text-base"></i>
          Translate into {selected.length > 0 ? `${selected.length} Language${selected.length > 1 ? 's' : ''}` : '...'} — Sign In
        </button>
      </div>
    </FeatureModal>
  );
}

// ─── 6. Publish & Sell Modal ──────────────────────────────────────────────────
function PublishModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [selected, setSelected] = useState<string | null>(null);

  const platforms = [
    { id: 'own', name: 'Your Website', icon: 'ri-global-line', color: '#6366f1', desc: 'Sell directly, 0% commission', fee: '0%', badge: 'Best' },
    { id: 'amazon', name: 'Amazon KDP', icon: 'ri-amazon-line', color: '#f59e0b', desc: 'Largest eBook marketplace', fee: '35–70%', badge: null },
    { id: 'apple', name: 'Apple Books', icon: 'ri-apple-line', color: '#1e293b', desc: '500M+ Apple devices', fee: '70%', badge: null },
    { id: 'google', name: 'Google Play', icon: 'ri-google-play-line', color: '#10b981', desc: 'Android & web readers', fee: '52%', badge: null },
    { id: 'kobo', name: 'Kobo / Rakuten', icon: 'ri-book-2-line', color: '#ef4444', desc: 'Global e-reader network', fee: '70%', badge: null },
    { id: 'draft2digital', name: 'Draft2Digital', icon: 'ri-share-line', color: '#8b5cf6', desc: 'Distribute everywhere at once', fee: '90%', badge: 'All-in-one' },
  ];

  const steps = ['Connect Platform', 'Configure Pricing', 'Publish eBook', 'Start Selling'];

  return (
    <FeatureModal onClose={onClose} width="max-w-2xl">
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500 flex items-center justify-center">
            <i className="ri-store-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Publish &amp; Sell</h2>
            <p className="text-sm text-slate-500">Distribute to Amazon, Apple Books, and more</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>

      <div className="p-6 space-y-6">
        {/* Publishing Steps */}
        <div className="flex items-center gap-0">
          {steps.map((step, idx) => (
            <div key={step} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold border-2 ${idx === 0 ? 'bg-indigo-500 border-indigo-500 text-white' : 'bg-white border-slate-200 text-slate-400'}`}>
                  {idx + 1}
                </div>
                <p className="text-xs text-slate-500 mt-1.5 text-center leading-tight whitespace-nowrap">{step}</p>
              </div>
              {idx < steps.length - 1 && <div className={`h-0.5 flex-1 mx-1 mb-4 ${idx === 0 ? 'bg-indigo-200' : 'bg-slate-200'}`}></div>}
            </div>
          ))}
        </div>

        {/* Platform cards */}
        <div>
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3">Select Distribution Channel</p>
          <div className="grid grid-cols-2 gap-3">
            {platforms.map(p => (
              <button
                key={p.id}
                onClick={() => setSelected(p.id === selected ? null : p.id)}
                className={`relative p-4 rounded-2xl border-2 text-left transition-all cursor-pointer ${selected === p.id ? 'border-current shadow-lg' : 'border-slate-200 hover:border-slate-300'}`}
                style={selected === p.id ? { borderColor: p.color, backgroundColor: p.color + '08' } : {}}
              >
                {p.badge && (
                  <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-bold rounded-full ${p.badge === 'Best' ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'}`}>{p.badge}</span>
                )}
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: p.color + '20' }}>
                    <i className={`${p.icon} text-lg`} style={{ color: p.color }}></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 leading-none">{p.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.desc}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Your royalty</span>
                  <span className="text-sm font-extrabold" style={{ color: p.color }}>{p.fee}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={onLogin}
          disabled={!selected}
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl cursor-pointer text-sm transition-colors"
        >
          <i className="ri-rocket-line text-base"></i>
          {selected ? `Publish on ${platforms.find(p => p.id === selected)?.name} — Sign In` : 'Select a Platform to Continue'}
        </button>
      </div>
    </FeatureModal>
  );
}

// ─── 7. Sales Analytics Modal ────────────────────────────────────────────────
function AnalyticsModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');

  const stats = [
    { label: 'Total Reads', value: '24,918', change: '+12.4%', icon: 'ri-eye-line', color: '#6366f1', up: true },
    { label: 'Downloads', value: '8,302', change: '+8.1%', icon: 'ri-download-line', color: '#10b981', up: true },
    { label: 'Revenue', value: '$4,216', change: '+21.3%', icon: 'ri-money-dollar-circle-line', color: '#f59e0b', up: true },
    { label: 'Countries', value: '67', change: '+5 new', icon: 'ri-global-line', color: '#ec4899', up: true },
  ];

  const barData = [
    { label: 'Mon', value: 68 }, { label: 'Tue', value: 82 }, { label: 'Wed', value: 71 }, { label: 'Thu', value: 95 },
    { label: 'Fri', value: 88 }, { label: 'Sat', value: 54 }, { label: 'Sun', value: 60 },
  ];

  const topEbooks = [
    { name: 'Startup Playbook', reads: 8412, rev: '$1,240', change: '+18%' },
    { name: 'Mindset Mastery', reads: 6890, rev: '$980', change: '+11%' },
    { name: 'Python Mastery Guide', reads: 4720, rev: '$830', change: '+24%' },
    { name: 'The Wealth Builder', reads: 2640, rev: '$640', change: '+9%' },
  ];

  return (
    <FeatureModal onClose={onClose} width="max-w-3xl">
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-pink-500 flex items-center justify-center">
            <i className="ri-bar-chart-2-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Sales Analytics</h2>
            <p className="text-sm text-slate-500">Track reads, downloads, and revenue in real time</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>

      <div className="p-6 space-y-6">
        {/* Period selector */}
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-slate-700">Overview</p>
          <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
            {(['7d', '30d', '90d'] as const).map(p => (
              <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${period === p ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                {p === '7d' ? 'Last 7 days' : p === '30d' ? 'Last 30 days' : 'Last 90 days'}
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {stats.map(s => (
            <div key={s.label} className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: s.color + '15' }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }}></i>
              </div>
              <p className="text-xl font-extrabold text-slate-900">{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              <p className={`text-xs font-bold mt-1 ${s.up ? 'text-emerald-600' : 'text-red-500'}`}>
                <i className={`${s.up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-xs`}></i> {s.change}
              </p>
            </div>
          ))}
        </div>

        {/* Bar chart */}
        <div className="bg-slate-50 rounded-2xl p-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4">Daily Downloads (this week)</p>
          <div className="flex items-end gap-2 h-28">
            {barData.map(d => (
              <div key={d.label} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-slate-400 font-semibold">{d.value}</span>
                <div
                  className="w-full rounded-t-lg bg-gradient-to-t from-pink-500 to-rose-400 transition-all duration-700"
                  style={{ height: `${(d.value / 100) * 80}%`, minHeight: '8px' }}
                ></div>
                <span className="text-xs text-slate-400">{d.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top eBooks */}
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Top Performing eBooks</p>
          <div className="space-y-2">
            {topEbooks.map((e, idx) => (
              <div key={e.name} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-6 h-6 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-extrabold text-pink-600">{idx + 1}</span>
                </div>
                <p className="flex-1 text-sm font-semibold text-slate-700 truncate">{e.name}</p>
                <span className="text-xs text-slate-400">{e.reads.toLocaleString()} reads</span>
                <span className="text-sm font-bold text-slate-900 w-16 text-right">{e.rev}</span>
                <span className="text-xs font-bold text-emerald-600 w-12 text-right">{e.change}</span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={onLogin}
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold rounded-xl cursor-pointer text-sm hover:opacity-90 transition-all shadow-lg shadow-pink-200"
        >
          <i className="ri-bar-chart-2-line text-base"></i>
          View Full Dashboard — Sign In
        </button>
      </div>
    </FeatureModal>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function FeatureHighlights({ onGenerateEbook, onBrowseTemplates, onLogin }: FeatureHighlightsProps) {
  type ModalType = 'ai-writing' | 'export' | 'collaboration' | 'cover' | 'translation' | 'publish' | 'analytics' | null;
  const [openModal, setOpenModal] = useState<ModalType>(null);

  const features: {
    icon: string; color: string; title: string; desc: string;
    action: string; modal: ModalType | 'browse' | 'generate';
    highlight: string;
  }[] = [
    {
      icon: 'ri-magic-line', color: '#f97316', title: 'AI Writing',
      desc: 'Generate full chapters, outlines, and summaries in seconds with OpenClaw AI.',
      action: 'Try AI Writing', modal: 'ai-writing', highlight: 'from-orange-50 to-amber-50 border-orange-200',
    },
    {
      icon: 'ri-palette-line', color: '#8b5cf6', title: 'Pro Templates',
      desc: 'Choose from 300+ professionally designed templates across every genre.',
      action: 'Browse Templates', modal: 'browse', highlight: 'from-purple-50 to-violet-50 border-purple-200',
    },
    {
      icon: 'ri-file-pdf-line', color: '#ef4444', title: 'Multi-Format Export',
      desc: 'Export your eBook to PDF, EPUB, DOCX, and Kindle-ready formats instantly.',
      action: 'Export Options', modal: 'export', highlight: 'from-red-50 to-rose-50 border-red-200',
    },
    {
      icon: 'ri-team-line', color: '#10b981', title: 'Collaboration',
      desc: 'Invite co-authors, editors, and reviewers to work on your eBook in real time.',
      action: 'Invite Team', modal: 'collaboration', highlight: 'from-emerald-50 to-teal-50 border-emerald-200',
    },
    {
      icon: 'ri-image-ai-line', color: '#0ea5e9', title: 'AI Cover Design',
      desc: 'Generate stunning, genre-specific cover art with a single click.',
      action: 'Design Cover', modal: 'cover', highlight: 'from-sky-50 to-cyan-50 border-sky-200',
    },
    {
      icon: 'ri-translate-2', color: '#f59e0b', title: 'Auto-Translation',
      desc: 'Translate your eBook into 50+ languages to reach a global audience.',
      action: 'Translate Now', modal: 'translation', highlight: 'from-amber-50 to-yellow-50 border-amber-200',
    },
    {
      icon: 'ri-store-line', color: '#6366f1', title: 'Publish & Sell',
      desc: 'Sell directly from your website or distribute to Amazon, Apple Books, and more.',
      action: 'Start Publishing', modal: 'publish', highlight: 'from-indigo-50 to-blue-50 border-indigo-200',
    },
    {
      icon: 'ri-bar-chart-2-line', color: '#ec4899', title: 'Sales Analytics',
      desc: 'Track reads, downloads, and revenue with a detailed analytics dashboard.',
      action: 'View Analytics', modal: 'analytics', highlight: 'from-pink-50 to-rose-50 border-pink-200',
    },
  ];

  const handleFeatureClick = (modal: ModalType | 'browse' | 'generate') => {
    if (modal === 'browse') { onBrowseTemplates(); }
    else if (modal === 'generate') { onGenerateEbook(); }
    else { setOpenModal(modal); }
  };

  return (
    <>
      <div className="bg-white border-t border-b border-slate-200 py-16 mt-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-50 border border-orange-200 rounded-full mb-4">
              <i className="ri-sparkling-2-line text-orange-500 text-sm"></i>
              <span className="text-sm font-semibold text-orange-700">Full Feature Suite</span>
            </div>
            <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Everything You Need to Publish</h2>
            <p className="text-slate-500 text-sm max-w-xl mx-auto">From AI writing to professional design, openclaw.ai handles the entire publishing workflow for you.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {features.map(feature => (
              <button
                key={feature.title}
                onClick={() => handleFeatureClick(feature.modal)}
                className={`group relative p-5 bg-gradient-to-br ${feature.highlight} border-2 rounded-2xl text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer overflow-hidden`}
              >
                {/* BG glow */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl"
                  style={{ background: `radial-gradient(circle at 30% 30%, ${feature.color}, transparent 70%)` }}
                ></div>

                <div className="relative z-10">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                    style={{ backgroundColor: feature.color }}
                  >
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm mb-2">{feature.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed mb-4">{feature.desc}</p>

                  {/* CTA row */}
                  <div
                    className="flex items-center gap-1.5 text-xs font-bold transition-all duration-300 group-hover:gap-2.5"
                    style={{ color: feature.color }}
                  >
                    <span>{feature.action}</span>
                    <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1"></i>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {openModal === 'ai-writing' && <AIWritingModal onClose={() => setOpenModal(null)} onGenerate={() => { setOpenModal(null); onGenerateEbook(); }} />}
      {openModal === 'export' && <ExportModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'collaboration' && <CollaborationModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'cover' && <CoverDesignModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'translation' && <TranslationModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'publish' && <PublishModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'analytics' && <AnalyticsModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
    </>
  );
}
